import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useHeatmapData, useHeatmapSummary, useExportHeatmapCSV, type HeatmapFilters } from '@/hooks/useAnalyticsHeatmap';
import { useCustomers } from '@/hooks/useCustomers';
import { useSuppliers } from '@/hooks/useSuppliers';
import { Loader2, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { KPICards } from '@/components/heatmap/KPICards';
import { BillDetailsModal } from '@/components/heatmap/BillDetailsModal';
import { HeatmapCell } from '@/components/heatmap/HeatmapCell';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function HeatMapPage() {
  const [filters, setFilters] = useState<HeatmapFilters>({ months: 12 });
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(12);
  const [selectedCell, setSelectedCell] = useState<any>(null);

  const { data, isLoading } = useHeatmapData({ ...filters, offset, limit });
  const { data: summaryData, isLoading: summaryLoading } = useHeatmapSummary(filters);
  const { customers } = useCustomers();
  const { suppliers } = useSuppliers();
  const exportMutation = useExportHeatmapCSV();

  const rows = data?.data || [];
  const months = data?.months || [];
  const totalCount = data?.total_count || 0;
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(totalCount / limit);

  const handleCellClick = (row: any, monthData: any) => {
    setSelectedCell({
      accountNumber: row.account_number,
      supplierName: row.supplier_name,
      customerName: row.customer_name,
      month: monthData.month,
      status: monthData.status,
      billId: monthData.bill_id,
      invoiceNumber: monthData.invoice_number,
      amount: monthData.amount_due
    });
  };

  const handleExport = () => {
    exportMutation.mutate(filters);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Bills Heatmap"
          description="Visualize bill availability status across accounts and months"
        />
        <Button onClick={handleExport} disabled={exportMutation.isPending}>
          {exportMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Download CSV
        </Button>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex gap-2">
          <div className="space-y-1">
            <Label htmlFor="customer-filter">Customer</Label>
            <Select
              value={filters.customer_id || 'all'}
              onValueChange={(value) => {
                setFilters(prev => ({ ...prev, customer_id: value === 'all' ? undefined : value }));
                setOffset(0);
              }}
            >
              <SelectTrigger className="w-[180px]" id="customer-filter">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {customers?.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="supplier-filter">Supplier</Label>
            <Select
              value={filters.supplier_id || 'all'}
              onValueChange={(value) => {
                setFilters(prev => ({ ...prev, supplier_id: value === 'all' ? undefined : value }));
                setOffset(0);
              }}
            >
              <SelectTrigger className="w-[180px]" id="supplier-filter">
                <SelectValue placeholder="All Suppliers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers?.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="account-filter">Account Number</Label>
            <Input
              id="account-filter"
              placeholder="Search account..."
              className="w-[180px]"
              value={filters.account_number || ''}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, account_number: e.target.value || undefined }));
                setOffset(0);
              }}
            />
          </div>
        </div>
        
        <div className="space-y-1">
          <Label htmlFor="months-filter">Period</Label>
          <Select
            value={filters.months?.toString() || '12'}
            onValueChange={(value) => {
              setFilters(prev => ({ ...prev, months: parseInt(value) as 3 | 6 | 12 }));
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-[150px]" id="months-filter">
              <SelectValue placeholder="Month Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Last 3 Months</SelectItem>
              <SelectItem value="6">Last 6 Months</SelectItem>
              <SelectItem value="12">Last 12 Months</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="limit-filter">Show</Label>
          <Select
            value={limit.toString()}
            onValueChange={(value) => {
              setLimit(parseInt(value));
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-[130px]" id="limit-filter">
              <SelectValue placeholder="Rows" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="12">12 rows</SelectItem>
              <SelectItem value="25">25 rows</SelectItem>
              <SelectItem value="50">50 rows</SelectItem>
              <SelectItem value="100">100 rows</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <KPICards
        totalCustomers={summaryData?.total_customers || 0}
        totalSuppliers={summaryData?.total_suppliers || 0}
        totalAccounts={summaryData?.total_accounts || 0}
        missingBillsCount={summaryData?.missing_bills_count || 0}
        isLoading={summaryLoading}
      />

      <Card>
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No data available for the selected filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background z-10 min-w-[150px]">Customer</TableHead>
                    <TableHead className="sticky left-[150px] bg-background z-10 min-w-[150px]">Supplier</TableHead>
                    <TableHead className="sticky left-[300px] bg-background z-10 min-w-[150px]">Account Number</TableHead>
                    {months.map((month: string) => (
                      <TableHead key={month} className="text-center min-w-[70px]">
                        {format(new Date(month), 'MMM')}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row: any) => (
                    <TableRow key={row.account_id}>
                      <TableCell className="sticky left-0 bg-background font-medium">
                        {row.customer_name}
                      </TableCell>
                      <TableCell className="sticky left-[150px] bg-background">
                        {row.supplier_name}
                      </TableCell>
                      <TableCell className="sticky left-[300px] bg-background font-mono text-sm">
                        {row.account_number}
                      </TableCell>
                      {row.months.map((monthData: any) => (
                        <TableCell key={monthData.month} className="text-center p-1">
                          <HeatmapCell
                            month={monthData.month}
                            status={monthData.status}
                            billCount={monthData.bill_count}
                            amount={monthData.amount_due}
                            onClick={() => handleCellClick(row, monthData)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              Showing {offset + 1}-{Math.min(offset + limit, totalCount)} of {totalCount} rows
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="text-sm">
                Page {currentPage} of {totalPages || 1}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= totalCount}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {selectedCell && (
        <BillDetailsModal
          open={!!selectedCell}
          onClose={() => setSelectedCell(null)}
          accountNumber={selectedCell.accountNumber}
          supplierName={selectedCell.supplierName}
          customerName={selectedCell.customerName}
          month={selectedCell.month}
          status={selectedCell.status}
          billId={selectedCell.billId}
          invoiceNumber={selectedCell.invoiceNumber}
          amount={selectedCell.amount}
        />
      )}
    </div>
  );
}
